import React from "react";
import { useAuth, useSignIn } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";

/**
 * The three Clerk social login buttons, shared by the Login and Register views.
 *
 * IMPORTANT: this component must only ever be RENDERED when clerkEnabled is true.
 * Unlike Auth0's useAuth0(), clerk-react's useSignIn() throws outside a
 * <ClerkProvider>, so calling it in a view that also serves the legacy
 * IdentityServer4 path would break that path entirely. Keeping the hook in a
 * conditionally-rendered child is what makes the flag safe.
 *
 * Each button goes straight to its provider rather than showing Clerk's account
 * picker, matching the legacy button UX. The Auth0 version passed
 * `prompt: "select_account"` for Google; the Clerk equivalent is configured on
 * the Google connection in the dashboard, not per-call.
 */

const OAUTH_REDIRECT = {
  // Completes the OAuth handshake (<AuthenticateWithRedirectCallback />).
  redirectUrl: "/sso-callback",
  // The provisioning gate that resolves our GUID into the token.
  redirectUrlComplete: "/auth/complete",
};

const buttonBaseSx = {
  width: "300px",
  height: "48px",
  borderRadius: "8px",
  paddingTop: 3,
  paddingBottom: 3,
};

const lightButtonSx = {
  ...buttonBaseSx,
  backgroundColor: "#ffffff",
  borderColor: "#000",
  "&:hover": {
    backgroundColor: "#c6c6c6",
  },
};

const rowSx = {
  marginLeft: "auto",
  marginRight: "auto",
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
};

const GoogleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    width="24px"
    height="24px"
  >
    <path
      fill="#FFC107"
      d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
    />
    <path
      fill="#FF3D00"
      d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
    />
    <path
      fill="#1976D2"
      d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg
    fill="#4a90e2"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    width="28px"
    height="28px"
  >
    <path d="M 7.5 1 C 3.9160714 1 1 3.9160714 1 7.5 C 1 11.083929 3.9160714 14 7.5 14 C 11.083929 14 14 11.083929 14 7.5 C 14 3.9160714 11.083929 1 7.5 1 z M 7.5 2 C 10.543488 2 13 4.4565116 13 7.5 C 13 10.266333 10.967571 12.541024 8.3125 12.933594 L 8.3125 9.0898438 L 9.8652344 9.0898438 L 10.109375 7.5136719 L 8.3125 7.5136719 L 8.3125 6.6503906 C 8.3125 5.9953906 8.5256719 5.4140625 9.1386719 5.4140625 L 10.123047 5.4140625 L 10.123047 4.0371094 C 9.9500469 4.0141094 9.5845781 3.9628906 8.8925781 3.9628906 C 7.4485781 3.9628906 6.6015625 4.7258906 6.6015625 6.4628906 L 6.6015625 7.5117188 L 5.1171875 7.5117188 L 5.1171875 9.0898438 L 6.6035156 9.0898438 L 6.6035156 12.919922 C 3.9897868 12.492118 2 10.237066 2 7.5 C 2 4.4565116 4.4565116 2 7.5 2 z" />
  </svg>
);

const AppleIcon = () => (
  <svg
    fill="#ffffff"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="22px"
    height="22px"
  >
    <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.983 4.46z" />
  </svg>
);

const ClerkSocialButtons = ({ labelPrefix = "", onError, children }) => {
  const { isLoaded, signIn } = useSignIn();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();

  const startOAuth = async (strategy) => {
    if (!isLoaded) {
      return;
    }
    // A Clerk session can already exist while the app still thinks we're logged
    // out -- most easily if the /auth/complete gate failed after Clerk had
    // finished authenticating. Calling authenticateWithRedirect in that state
    // throws "You're already signed in", which strands the user on the login
    // screen with no way forward. Send them back through the gate instead: it
    // is idempotent, and for an already-resolved user it is a single `if`.
    if (isSignedIn) {
      // Reaching here means we are recovering from a live Clerk session the app
      // never completed -- worth knowing, because it is a symptom of an earlier
      // failure rather than a normal login.
      console.log(
        "[login] Clerk session already active; re-entering /auth/complete " +
          "instead of starting a new OAuth flow."
      );
      navigate("/auth/complete");
      return;
    }
    try {
      await signIn.authenticateWithRedirect({ strategy, ...OAUTH_REDIRECT });
    } catch (err) {
      console.log(err);
      if (onError) {
        onError(err);
      }
    }
  };

  return (
    <>
      {/* Google */}
      <Box sx={rowSx}>
        <Button
          variant="outlined"
          disabled={!isLoaded}
          onClick={() => startOAuth("oauth_google")}
          sx={lightButtonSx}
        >
          <GoogleIcon />
          <Typography
            variant="h6"
            sx={{ marginLeft: 2, color: "#000", textTransform: "none" }}
          >
            {labelPrefix}Google
          </Typography>
        </Button>
      </Box>

      {/* Facebook */}
      <Box sx={{ ...rowSx, marginTop: "1em" }}>
        <Button
          variant="outlined"
          disabled={!isLoaded}
          onClick={() => startOAuth("oauth_facebook")}
          sx={lightButtonSx}
        >
          <FacebookIcon />
          <Typography
            variant="h6"
            sx={{ marginLeft: 2, color: "#000", textTransform: "none" }}
          >
            {labelPrefix}Facebook
          </Typography>
        </Button>
      </Box>

      {/* Apple */}
      <Box sx={{ ...rowSx, marginTop: "1em" }}>
        <Button
          variant="contained"
          disabled={!isLoaded}
          onClick={() => startOAuth("oauth_apple")}
          sx={{
            ...buttonBaseSx,
            backgroundColor: "#000000",
            "&:hover": {
              backgroundColor: "#333333",
            },
          }}
        >
          <AppleIcon />
          <Typography
            variant="h6"
            sx={{ marginLeft: 2, color: "#fff", textTransform: "none" }}
          >
            {labelPrefix}Apple
          </Typography>
        </Button>
      </Box>

      {children}
    </>
  );
};

export default ClerkSocialButtons;
